import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild, forwardRef, inject } from "@angular/core"
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms"
import { IconImageService } from "@shared/services/icon-image.service"
import { FlowHelperTextComponent, HelperTextVariant } from "../flow-helper-text/flow-helper-text.component"

export type FlowTextInputType = "text" | "number" | "time"

let nextId = 0

@Component({
  selector: "flow-text-input",
  templateUrl: "./flow-text-input.component.html",
  styleUrl: "./flow-text-input.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FlowTextInputComponent),
      multi: true,
    },
  ],
})
export class FlowTextInputComponent implements ControlValueAccessor {
  iconService = inject(IconImageService)

  @Input() label: string = ""
  @Input() value: string = ""
  @Input() placeholder: string = "Placeholder text"
  @Input() type: FlowTextInputType = "text"
  @Input() disabled: boolean = false
  @Input() error: boolean = false
  @Input() success: boolean = false
  @Input() helperText: string = ""
  @Input() maxLength: number | null = null
  @Input() showRevealToggle: boolean = false
  @Input() suffixLabel: string = ""
  @Input() hideClear: boolean = false
  @Input() width: number | null = null
  @Input() showColorBox: boolean = false
  @Input() defaultColorBox: string = "#222222"

  @Output() valueChange = new EventEmitter<string>()
  @Output() valueCommitted = new EventEmitter<string>()

  public focused: boolean = false
  public revealed: boolean = false

  public readonly fieldId = `flow-text-input-${nextId++}`

  @ViewChild(FlowHelperTextComponent) private helper?: FlowHelperTextComponent

  private onChange: (value: string) => void = () => {}
  private onTouched: () => void = () => {}

  public writeValue(value: string): void {
    this.value = value ?? ""
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  public get inputType(): string {
    return this.showRevealToggle && !this.revealed ? "password" : "text"
  }

  public get inputMode(): string {
    return this.type === "number" || this.type === "time" ? "numeric" : "text"
  }

  public get effectivePlaceholder(): string {
    return this.type === "time" ? "HH:MM" : this.placeholder
  }

  public get showClock(): boolean {
    return this.type === "time"
  }

  public get timeError(): boolean {
    if (this.type !== "time" || this.value.length === 0) return false
    return !this.isValidTime(this.value)
  }

  public get characterCount(): number | null {
    return this.maxLength === null ? null : this.value.length
  }

  public get showHelper(): boolean {
    return !!this.helperText || this.maxLength !== null
  }

  public get helperVariant(): HelperTextVariant {
    if (this.showError) return "destructive"
    return this.success ? "success" : "default"
  }

  public get colorError(): boolean {
    if (!this.showColorBox) return false
    const value = this.displayValue
    if (value === "#") return false
    return !this.isValidHex(value)
  }

  public get showError(): boolean {
    return this.error || this.colorError || this.timeError
  }

  public get showClear(): boolean {
    if (this.showColorBox || this.type === "time") return false
    return !this.hideClear && !this.disabled && this.value.length > 0
  }

  public get hasValue(): boolean {
    return this.value.length > 0
  }

  public get revealIcon(): string {
    return this.revealed ? this.iconService.Icon.eye_open : this.iconService.Icon.eye_closed
  }

  public get displayValue(): string {
    return this.showColorBox ? this.formatHex(this.value) : this.value
  }

  public get colorBoxColor(): string {
    if (!this.showColorBox) return this.defaultColorBox
    return this.isValidHex(this.displayValue) ? this.displayValue : this.defaultColorBox
  }

  private formatHex(value: string): string {
    return "#" + value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6)
  }

  private isValidHex(value: string): boolean {
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)
  }

  private isValidTime(value: string): boolean {
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(value)
  }

  private formatTime(raw: string): string {
    const digits = raw.replace(/\D/g, "").slice(0, 4)
    let out = ""
    for (let i = 0; i < digits.length; i++) {
      const digit = +digits[i]
      if (i === 0 && digit > 2) break
      if (i === 1 && out === "2" && digit > 3) break
      if (i === 2 && digit > 5) break
      if (i === 2) out += ":"
      out += digits[i]
    }
    return out
  }

  public onKeydown(event: KeyboardEvent, field: HTMLInputElement): void {
    if (event.key === "Enter") {
      this.valueCommitted.emit(this.value)
    }

    if (this.maxLength === null) return
    if (event.ctrlKey || event.metaKey || event.altKey) return
    if (event.key.length !== 1) return

    const hasSelection = field.selectionStart !== field.selectionEnd
    if (!hasSelection && field.value.length >= this.maxLength) {
      this.helper?.flash()
    }
  }

  public onInput(raw: string, field: HTMLInputElement): void {
    let next: string
    if (this.showColorBox) {
      next = this.formatHex(raw)
      if (field.value !== next) field.value = next
    } else if (this.type === "time") {
      next = this.formatTime(raw)
      if (field.value !== next) field.value = next
    } else {
      next = this.type === "number" ? raw.replace(/[^0-9]/g, "") : raw
    }
    this.value = next
    this.valueChange.emit(next)
    this.onChange(next)
  }

  public onFocus(): void {
    this.focused = true
  }

  public onBlur(): void {
    this.focused = false
    this.onTouched()
    this.valueCommitted.emit(this.value)
  }

  public onClear(): void {
    if (this.disabled) return
    this.value = ""
    this.valueChange.emit("")
    this.valueCommitted.emit("")
    this.onChange("")
  }

  public toggleReveal(): void {
    if (this.disabled) return
    this.revealed = !this.revealed
  }
}
